/**
 * ResourceSystem.ts
 * Manages the four resources: Reputation, Fanbase, Sales, Gear (§4).
 *
 * Income model:
 *   - 1-second passive tick: base rates + venue bonuses
 *   - Fanbase creeps toward fanbaseCap; Sales scales with Fanbase
 *   - Enemy kills reward Reputation + Sales
 *
 * Stage 6: venue bonuses are baked in as constants at construction time.
 * Stage 7+: BuildSystem calls addVenueBonus() as buildings are placed.
 */

import type { BalanceValues, EnemyType, ResourceType } from '../types';
import {
  FANBASE_STARTING_CAP,
  REP_MAX, SALES_MAX, GEAR_MAX,
  FAN_KILL_STEAL_SALES,
  FAN_KILL_STEAL_GEAR,
  FAN_KILL_STEAL_FANBASE,
  FAN_KILL_REP_LOSS,
  VENUE_LOST_REP_LOSS,
  SCENE_BUZZ_REP_REQUIRED,
  SCENE_BUZZ_FAN_REQUIRED,
  SCENE_BREAKOUT_REP_REQUIRED,
  SCENE_BREAKOUT_FAN_REQUIRED,
  SCENE_LEGEND_REP_REQUIRED,
  SCENE_LEGEND_FAN_REQUIRED,
  EXPANSION_BUILD_FAN_REQUIRED,
  FAN_DECAY_PER_SEC,
  SALES_UPKEEP_PER_SEC,
  MUSIC_PRESS_BAD_REVIEW_MULT,
} from '../constants';

export type SceneTier = 'local' | 'buzz' | 'breakout' | 'legend';

export interface ProgressionState {
  tier: SceneTier;
  label: string;
  sceneScore: number;
  peakReputation: number;
  peakFanbase: number;
  canUpgradeLevel2: boolean;
  canUpgradeLevel3: boolean;
  canBuildExpansion: boolean;
  nextGoal: string;
}

export class ResourceSystem {
  // ── State — set from balance in constructor, overwritten by loadGame() ──
  reputation    = 0;
  fanbase       = 0;
  fanbaseCap    = FANBASE_STARTING_CAP;
  /** Extra cap added by player-built Flyer Posts (PlacementSystem writes this). */
  extraFanbaseCap = 0;
  sales         = 0;
  gear          = 0;

  // ── Progression memory ───────────────────────────────────────────────
  // Current resources are spendable. These values remember earned progress
  // so unlocks do not disappear after the player invests in venues/upgrades.
  peakReputation = 0;
  peakFanbase    = 0;
  sceneScore     = 0;

  // ── Per-second income accumulators ────────────────────────────────────
  private repPerSec   = 0;
  private salesPerSec = 0;
  private fanPerSec   = 0;
  private gearPerSec  = 0;

  // ── Beacon signal modifiers (set by BeaconSystem on band change) ───────
  private signalRepMult    = 1.0; // multiplier for rep income (< 1.0 in reduced/critical bands)
  private signalSalesDrain = 0;   // sales/s drained (> 0 in critical band)
  private enemySalesDrain  = 0;   // sales/s drained by living Streaming Moguls
  private badReviewTimer   = 0;   // Music Press penalty remaining (seconds)

  private tickTimer = 0;
  private readonly TICK = 1.0; // seconds between income pulses

  constructor(private readonly balance: BalanceValues) {
    // Starting resources from DB-tunable balance (falls back to bundled defaults)
    this.reputation = balance.startingReputation;
    this.fanbase    = balance.startingFanbase;
    this.sales      = balance.startingSales;
    this.gear       = balance.startingGear;
    this.peakReputation = this.reputation;
    this.peakFanbase    = this.fanbase;
    this.recalculateSceneScore();
    // Passive income rates
    this.repPerSec   = balance.resRepBaseIncome;
    this.salesPerSec = balance.resSalesBaseIncome;
  }

  // ── Game loop ─────────────────────────────────────────────────────────

  update(delta: number): void {
    if (this.badReviewTimer > 0) {
      this.badReviewTimer = Math.max(0, this.badReviewTimer - delta);
    }
    this.tickTimer += delta;
    if (this.tickTimer >= this.TICK) {
      this.tickTimer -= this.TICK;
      this.applyTick();
    }
  }

  private applyTick(): void {
    // Reputation income is multiplied by the signal band modifier.
    // Critical / enemy-held Beacon sets this to 0 so public momentum stalls.
    this.awardReputation(this.repPerSec * this.signalRepMult);

    // Fans drift away over time — players need to re-engage venues to hold the crowd.
    this.fanbase = Math.max(0, this.fanbase - FAN_DECAY_PER_SEC);

    if (this.fanPerSec > 0 && this.fanbase < this.fanbaseCap + this.extraFanbaseCap) {
      this.awardFanbase(this.fanPerSec);
    }

    const salesIncome = this.salesPerSec + Math.floor(this.fanbase * this.balance.resSalesFanMult);
    // Running a band costs money every second — upkeep on top of any drain from
    // critical-band signal loss.
    let salesNet = salesIncome - this.signalSalesDrain - this.enemySalesDrain - SALES_UPKEEP_PER_SEC;
    if (salesNet >= 0) {
      this.sales = Math.min(this.sales + salesNet, SALES_MAX);
    } else {
      // Net negative: upkeep or signal drain exceeds income
      this.sales = Math.max(0, this.sales + salesNet);
    }

    if (this.gearPerSec > 0) {
      this.gear = Math.min(this.gear + this.gearPerSec, GEAR_MAX);
    }
  }

  // ── Kill rewards ──────────────────────────────────────────────────────

  /** Award Reputation and Sales when an enemy is killed. */
  onEnemyKilled(enemyType: EnemyType | null, displayName = ''): void {
    const isMogul = enemyType === 'StreamingMogul' || displayName.includes('Mogul');
    const isHeavy = isMogul || enemyType === 'TributeAct';
    this.awardReputation(isHeavy ? this.balance.killRepMogul : this.balance.killRepSwarm);
    this.sales      = Math.min(this.sales      + (isHeavy ? this.balance.killSalesMogul : this.balance.killSalesSwarm), SALES_MAX);
  }

  /** Remove resources when enemies kill fan units during raids. */
  onFanKilledByEnemy(): void {
    this.sales   = Math.max(0, this.sales - FAN_KILL_STEAL_SALES);
    this.gear    = Math.max(0, this.gear - FAN_KILL_STEAL_GEAR);
    this.fanbase = Math.max(0, this.fanbase - FAN_KILL_STEAL_FANBASE);
    this.loseReputation(FAN_KILL_REP_LOSS);
  }

  /** Reputation hit when a claimed venue is knocked back to unclaimed. */
  onVenueLostToEnemy(): void {
    this.loseReputation(VENUE_LOST_REP_LOSS);
  }

  // ── Progression gates ────────────────────────────────────────────────

  get progression(): ProgressionState {
    const canUpgradeLevel2 =
      this.peakReputation >= SCENE_BUZZ_REP_REQUIRED ||
      this.peakFanbase >= SCENE_BUZZ_FAN_REQUIRED;
    const canUpgradeLevel3 =
      this.peakReputation >= SCENE_BREAKOUT_REP_REQUIRED &&
      this.peakFanbase >= SCENE_BREAKOUT_FAN_REQUIRED;
    const canBuildExpansion = this.peakFanbase >= EXPANSION_BUILD_FAN_REQUIRED;

    let tier: SceneTier = 'local';
    let label = 'Local Scene';
    if (this.peakReputation >= SCENE_LEGEND_REP_REQUIRED && this.peakFanbase >= SCENE_LEGEND_FAN_REQUIRED) {
      tier = 'legend';
      label = 'Future Legend';
    } else if (canUpgradeLevel3) {
      tier = 'breakout';
      label = 'Breakout Scene';
    } else if (canUpgradeLevel2) {
      tier = 'buzz';
      label = 'Buzz Building';
    }

    return {
      tier,
      label,
      sceneScore: this.sceneScore,
      peakReputation: Math.floor(this.peakReputation),
      peakFanbase: Math.floor(this.peakFanbase),
      canUpgradeLevel2,
      canUpgradeLevel3,
      canBuildExpansion,
      nextGoal: this.nextProgressionGoal(canUpgradeLevel2, canUpgradeLevel3),
    };
  }

  // ── Beacon signal hook (Stage 9) ─────────────────────────────────────

  /**
   * Called by BeaconSystem whenever the signal band changes.
   * @param repMult          Multiplier for rep income (1.0 = normal, 0.75 = reduced/critical)
   * @param salesDrainPerSec Sales drained per income tick (0 in normal/reduced bands)
   */
  setSignalModifiers(repMult: number, salesDrainPerSec: number): void {
    this.signalRepMult    = repMult;
    this.signalSalesDrain = salesDrainPerSec;
  }

  /** Apply the current global sales interference generated by Streaming Moguls. */
  setEnemySalesDrain(salesDrainPerSec: number): void {
    this.enemySalesDrain = Math.max(0, salesDrainPerSec);
  }

  /** Start or extend the timed Music Press bad-review penalty. */
  startBadReview(duration: number): void {
    this.badReviewTimer = Math.max(this.badReviewTimer, Math.max(0, duration));
  }

  restoreBadReview(duration: number): void {
    this.badReviewTimer = Math.max(0, duration);
  }

  get badReviewRemaining(): number { return this.badReviewTimer; }

  /** Venue REP path, allowing bad reviews to suppress Pubs without touching other rewards. */
  addVenueReputation(amount: number, isPub: boolean): void {
    const multiplier = isPub && this.badReviewTimer > 0 ? MUSIC_PRESS_BAD_REVIEW_MULT : 1;
    this.awardReputation(amount * multiplier);
  }

  /** Ambient REP sources such as movement should pause during Beacon crisis. */
  canEarnAmbientReputation(): boolean {
    return this.signalRepMult > 0;
  }

  // ── Building hook (Stage 7+) ──────────────────────────────────────────

  /**
   * Register a venue building's per-second yield.
   * Called by BuildSystem when a building becomes active.
   */
  addVenueBonus(rep = 0, sales = 0, fan = 0, gear = 0): void {
    this.repPerSec   += rep;
    this.salesPerSec += sales;
    this.fanPerSec   += fan;
    this.gearPerSec  += gear;
  }

  // ── Spending API ──────────────────────────────────────────────────────

  canAfford(cost: Partial<Record<ResourceType, number>>): boolean {
    if (cost.reputation != null && this.reputation < cost.reputation) return false;
    if (cost.fanbase    != null && this.fanbase    < cost.fanbase)    return false;
    if (cost.sales      != null && this.sales      < cost.sales)      return false;
    if (cost.gear       != null && this.gear       < cost.gear)       return false;
    return true;
  }

  spend(cost: Partial<Record<ResourceType, number>>): boolean {
    if (!this.canAfford(cost)) return false;
    if (cost.reputation) this.reputation -= cost.reputation;
    if (cost.fanbase)    this.fanbase    -= cost.fanbase;
    if (cost.sales)      this.sales      -= cost.sales;
    if (cost.gear)       this.gear       -= cost.gear;
    return true;
  }

  add(type: ResourceType, amount: number): void {
    switch (type) {
      case 'reputation': this.awardReputation(amount); break;
      case 'fanbase':    this.awardFanbase(amount);    break;
      case 'sales':      this.sales      = Math.min(this.sales      + amount, SALES_MAX);       break;
      case 'gear':       this.gear       = Math.min(this.gear       + amount, GEAR_MAX);        break;
    }
  }

  loseReputation(amount: number): void {
    this.reputation = Math.max(0, this.reputation - Math.max(0, amount));
  }

  syncProgressionPeaks(): void {
    this.peakReputation = Math.max(this.peakReputation, this.reputation);
    this.peakFanbase    = Math.max(this.peakFanbase, this.fanbase);
    this.recalculateSceneScore();
  }

  /** Snapshot for save state / HUD display. */
  get snapshot() {
    return {
      reputation: Math.floor(this.reputation),
      fanbase:    Math.floor(this.fanbase),
      fanbaseCap: this.fanbaseCap,
      sales:      Math.floor(this.sales),
      gear:       Math.floor(this.gear),
      peakReputation: Math.floor(this.peakReputation),
      peakFanbase:    Math.floor(this.peakFanbase),
      sceneScore:     Math.floor(this.sceneScore),
      badReviewRemaining: this.badReviewTimer,
    };
  }

  private awardReputation(amount: number): void {
    if (amount <= 0) return;
    this.reputation = Math.min(this.reputation + amount, REP_MAX);
    this.peakReputation = Math.max(this.peakReputation, this.reputation);
    this.recalculateSceneScore();
  }

  private awardFanbase(amount: number): void {
    if (amount <= 0) return;
    this.fanbase = Math.min(this.fanbase + amount, this.fanbaseCap + this.extraFanbaseCap);
    this.peakFanbase = Math.max(this.peakFanbase, this.fanbase);
    this.recalculateSceneScore();
  }

  private recalculateSceneScore(): void {
    this.sceneScore = Math.floor(this.peakReputation + this.peakFanbase * 2);
  }

  private nextProgressionGoal(canUpgradeLevel2: boolean, canUpgradeLevel3: boolean): string {
    if (!canUpgradeLevel2) {
      return `${SCENE_BUZZ_REP_REQUIRED} peak REP or ${SCENE_BUZZ_FAN_REQUIRED} peak FAN unlocks Level 2 venues`;
    }
    if (this.peakFanbase < EXPANSION_BUILD_FAN_REQUIRED) {
      return `${EXPANSION_BUILD_FAN_REQUIRED} peak FAN unlocks fan-built expansion venues`;
    }
    if (!canUpgradeLevel3) {
      return `${SCENE_BREAKOUT_REP_REQUIRED} peak REP + ${SCENE_BREAKOUT_FAN_REQUIRED} peak FAN unlocks Level 3 venues`;
    }
    if (this.peakReputation < SCENE_LEGEND_REP_REQUIRED || this.peakFanbase < SCENE_LEGEND_FAN_REQUIRED) {
      return `${SCENE_LEGEND_REP_REQUIRED} peak REP + ${SCENE_LEGEND_FAN_REQUIRED} peak FAN marks legendary status`;
    }
    return 'Legendary scene established — survive and protect the Beacon';
  }
}
